import React from 'react';
import { IoNotificationsOutline, IoNotifications } from 'react-icons/io5';
import { useNotifications } from '../../hooks/useNotifications';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { NOTIFICATION_FLAGS } from '../../utils/featureFlags';

interface NotificationBellProps {
  onClick: () => void;
  collapsedMode?: boolean;
  className?: string;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  onClick,
  collapsedMode = false,
  className = ''
}) => {
  const { unreadCount, isLoading } = useNotifications();
  const isBellEnabled = useFeatureFlag(NOTIFICATION_FLAGS.BELL_NOTIFICATIONS_ENABLED);

  // Don't render if bell notifications are disabled
  if (!isBellEnabled) {
    return null;
  }

  const hasUnread = unreadCount > 0;
  const displayCount = unreadCount > 99 ? '99+' : unreadCount.toString();

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={onClick}
        className={`
          flex items-center justify-center p-2 rounded-lg transition-all duration-200
          hover:bg-gray-100 dark:hover:bg-gray-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          ${collapsedMode ? 'w-full' : 'w-auto'}
          ${hasUnread ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}
        `}
        aria-label={`Notifications ${hasUnread ? `(${unreadCount} unread)` : ''}`}
        title={hasUnread ? `${unreadCount} unread notifications` : 'Notifications'}
      >
        {/* Bell Icon */}
        <div className="relative">
          {hasUnread ? (
            <IoNotifications className="w-6 h-6" />
          ) : (
            <IoNotificationsOutline className="w-6 h-6" />
          )}
          
          {/* Unread Badge */}
          {hasUnread && (
            <span 
              className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1"
              aria-hidden="true"
            >
              {displayCount}
            </span>
          )}
          
          {/* Loading Indicator */}
          {isLoading && (
            <div className="absolute -top-1 -right-1 w-3 h-3">
              <div className="w-full h-full border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Text Label (only in expanded mode) */}
        {!collapsedMode && (
          <span className="ml-2 text-sm font-medium">
            Notifications
          </span>
        )}
      </button>

      {/* Tooltip for collapsed mode */}
      {collapsedMode && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
          {hasUnread ? `${unreadCount} unread notifications` : 'Notifications'}
        </div>
      )}
    </div>
  );
}; 